import { TrendingUp, TrendingDown, Minus, ArrowUpRight } from "lucide-react";

/**
 * Carte d'indicateur de tension épidémiologique pour une pathologie.
 *
 * Composant du sous-bloc B1 (situation actuelle dans le département).
 *
 * Visuel sobre Cabinet : barre 4 segments ivoire/bordeaux, libellé Playfair,
 * trend Lucide discret. Pas de carte rouge, "très élevée" = bordeaux foncé.
 */

const PATHOLOGY_LABELS = {
  grippe: "Grippe",
  gastro: "Gastro-entérite",
  covid: "COVID-19",
  bronchiolite: "Bronchiolite",
  allergie: "Allergies saisonnières",
};

const DEMAND_LABELS = {
  low: "Faible",
  moderate: "Modérée",
  high: "Élevée",
  very_high: "Très élevée",
};

const DEMAND_SEGMENTS = { low: 1, moderate: 2, high: 3, very_high: 4 };

const TREND_META = {
  declining: { Icon: TrendingDown, label: "En baisse", color: "text-cabinet-forest" },
  stable: { Icon: Minus, label: "Stable", color: "text-cabinet-mute-1" },
  rising: { Icon: TrendingUp, label: "En hausse", color: "text-cabinet-bordeaux" },
  sharp_rise: { Icon: ArrowUpRight, label: "Forte hausse", color: "text-cabinet-bordeaux" },
};

const SOURCE_LABELS = {
  sentinelles: "Réseau Sentinelles",
  spf: "Santé Publique France",
  meteo_france: "Météo France",
};


export function TensionCard({ indicator }) {
  const label = PATHOLOGY_LABELS[indicator.pathologie] || indicator.pathologie;
  const demandLabel = DEMAND_LABELS[indicator.demand_level] || indicator.demand_level;
  const activeSegments = DEMAND_SEGMENTS[indicator.demand_level] || 0;
  const trend = TREND_META[indicator.trend] || TREND_META.stable;
  const TrendIcon = trend.Icon;

  const sourcesUsed = Array.from(
    new Set((indicator.signals || []).map((s) => s.source))
  );

  return (
    <article
      className="bg-white border border-cabinet-mute-2/40 rounded-lg px-6 py-5 transition-colors hover:border-cabinet-bordeaux/30"
      data-testid={`tension-card-${indicator.pathologie}`}
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-playfair text-lg text-cabinet-ink font-medium tracking-tight leading-snug">
            {label}
          </h3>
          <p
            className="mt-1 text-[12px] tracking-wide text-cabinet-mute-1"
            data-testid="tension-card-demand-label"
          >
            Tension {demandLabel.toLowerCase()}
          </p>
        </div>
        <div
          className={`flex-shrink-0 inline-flex items-center gap-1 text-[12px] font-medium ${trend.color}`}
          data-testid="tension-card-trend"
          title={trend.label}
        >
          <TrendIcon size={14} strokeWidth={2} />
          {trend.label}
        </div>
      </div>

      {/* Barre de niveau : 4 segments ivoire/bordeaux */}
      <div
        className="flex items-center gap-1.5"
        role="meter"
        aria-valuemin="0"
        aria-valuemax="4"
        aria-valuenow={activeSegments}
        aria-label={`Niveau de tension ${demandLabel}`}
        data-testid="tension-card-bar"
      >
        {[1, 2, 3, 4].map((seg) => {
          const isActive = seg <= activeSegments;
          const isVeryHigh = activeSegments === 4 && isActive;
          return (
            <span
              key={seg}
              className={`h-1.5 flex-1 rounded-sm transition-colors ${
                isActive
                  ? isVeryHigh
                    ? "bg-cabinet-bordeaux"
                    : "bg-cabinet-bordeaux/70"
                  : "bg-cabinet-mute-2/35"
              }`}
            />
          );
        })}
      </div>

      {/* Sources utilisées + score composite (discret) */}
      <div className="mt-4 flex items-center justify-between gap-3 text-[11.5px] tracking-wide text-cabinet-mute-1">
        <span data-testid="tension-card-sources">
          {sourcesUsed.length > 0
            ? `Sources : ${sourcesUsed.map((s) => SOURCE_LABELS[s] || s).join(" · ")}`
            : "Aucune source disponible"}
        </span>
        {typeof indicator.composite_score === "number" && (
          <span className="text-cabinet-ink font-medium tracking-wider">
            {Math.round(indicator.composite_score)}
            <span className="text-cabinet-mute-1 font-normal">/100</span>
          </span>
        )}
      </div>
    </article>
  );
}
